// PLX Data Parser - Decodes medical data from pulse oximeter
const parsePLXData = (
  dataView: DataView,
  serviceUuid: string,
  characteristicUuid: string
) => {
  // Only parse PLX service data
  if (!serviceUuid.toLowerCase().includes("1822")) {
    return; // Not PLX data, skip parsing
  }

  console.log(`\n🩺 PARSING PLX MEDICAL DATA:`);
  console.log(`   Service: ${serviceUuid}`);
  console.log(`   Characteristic: ${characteristicUuid}`);
  console.log(`   Data Length: ${dataView.byteLength} bytes`);

  try {
    let offset = 0;
    let spO2Normal: number | null = null;
    let pulseRateNormal: number | null = null;
    let spO2Fast: number | null = null;
    let pulseRateFast: number | null = null;

    // Read flags (1 byte)
    const flags = dataView.getUint8(offset);
    offset += 1;

    console.log(
      `   🚩 Flags: 0x${flags.toString(16).toUpperCase()} (${flags})`
    );

    // Parse flag bits
    const hasSpO2PRFast = (flags & 0x01) !== 0;
    const hasSpO2PRSlow = (flags & 0x02) !== 0;
    const hasMeasurementStatus = (flags & 0x04) !== 0;
    const hasDeviceSensorStatus = (flags & 0x08) !== 0;
    const hasPulseAmplitudeIndex = (flags & 0x10) !== 0;

    console.log(`   📋 Optional fields:`);
    console.log(`      SpO2PR-Fast: ${hasSpO2PRFast ? "✅" : "❌"}`);
    console.log(`      SpO2PR-Slow: ${hasSpO2PRSlow ? "✅" : "❌"}`);
    console.log(
      `      Measurement Status: ${hasMeasurementStatus ? "✅" : "❌"}`
    );
    console.log(
      `      Device/Sensor Status: ${hasDeviceSensorStatus ? "✅" : "❌"}`
    );
    console.log(
      `      Pulse Amplitude Index: ${hasPulseAmplitudeIndex ? "✅" : "❌"}`
    );

    // Helper function to parse SFLOAT (16-bit medical format)
    const parseSFLOAT = (value: number): number | null => {
      // Handle special values
      if (value === 0x07ff) return null; // NaN
      if (value === 0x0800) return null; // NRes
      if (value === 0x07fe) return Number.POSITIVE_INFINITY;
      if (value === 0x0802) return Number.NEGATIVE_INFINITY;

      // Extract mantissa (12 bits, signed)
      let mantissa = value & 0x0fff;
      if (mantissa >= 0x0800) {
        mantissa = mantissa - 0x1000; // Convert to signed
      }

      // Extract exponent (4 bits, signed)
      let exponent = (value >> 12) & 0x0f;
      if (exponent >= 0x08) {
        exponent = exponent - 0x10; // Convert to signed
      }

      // Calculate final value
      return mantissa * Math.pow(10, exponent);
    };

    // Read SpO2PR-Normal field (mandatory - 4 bytes)
    if (offset + 4 <= dataView.byteLength) {
      const spO2Raw = dataView.getUint16(offset, true); // Little endian
      offset += 2;
      const pulseRateRaw = dataView.getUint16(offset, true);
      offset += 2;

      spO2Normal = parseSFLOAT(spO2Raw);
      pulseRateNormal = parseSFLOAT(pulseRateRaw);

      console.log(`\n   💗 MAIN READINGS (Normal):`);
      console.log(
        `      SpO2: ${
          spO2Normal !== null ? spO2Normal.toFixed(1) + "%" : "N/A"
        }`
      );
      console.log(
        `      Pulse Rate: ${
          pulseRateNormal !== null ? pulseRateNormal.toFixed(0) + " BPM" : "N/A"
        }`
      );
    }

    // Read SpO2PR-Fast field (if present)
    if (hasSpO2PRFast && offset + 4 <= dataView.byteLength) {
      const spO2FastRaw = dataView.getUint16(offset, true);
      offset += 2;
      const pulseRateFastRaw = dataView.getUint16(offset, true);
      offset += 2;

      spO2Fast = parseSFLOAT(spO2FastRaw);
      pulseRateFast = parseSFLOAT(pulseRateFastRaw);

      console.log(`\n   ⚡ FAST READINGS:`);
      console.log(
        `      SpO2 Fast: ${
          spO2Fast !== null ? spO2Fast.toFixed(1) + "%" : "N/A"
        }`
      );
      console.log(
        `      Pulse Rate Fast: ${
          pulseRateFast !== null ? pulseRateFast.toFixed(0) + " BPM" : "N/A"
        }`
      );
    }

    // Read SpO2PR-Slow field (if present)
    if (hasSpO2PRSlow && offset + 4 <= dataView.byteLength) {
      const spO2SlowRaw = dataView.getUint16(offset, true);
      offset += 2;
      const pulseRateSlowRaw = dataView.getUint16(offset, true);
      offset += 2;

      const spO2Slow = parseSFLOAT(spO2SlowRaw);
      const pulseRateSlow = parseSFLOAT(pulseRateSlowRaw);

      console.log(`\n   🐌 SLOW READINGS:`);
      console.log(
        `      SpO2 Slow: ${
          spO2Slow !== null ? spO2Slow.toFixed(1) + "%" : "N/A"
        }`
      );
      console.log(
        `      Pulse Rate Slow: ${
          pulseRateSlow !== null ? pulseRateSlow.toFixed(0) + " BPM" : "N/A"
        }`
      );
    }

    // Read Measurement Status field (if present - 2 bytes)
    if (hasMeasurementStatus && offset + 2 <= dataView.byteLength) {
      const measurementStatus = dataView.getUint16(offset, true);
      offset += 2;

      console.log(
        `\n   📊 MEASUREMENT STATUS: 0x${measurementStatus
          .toString(16)
          .toUpperCase()}`
      );

      // Decode status bits
      const statusFlags = [];
      if (measurementStatus & 0x0020) statusFlags.push("Measurement Ongoing");
      if (measurementStatus & 0x0040) statusFlags.push("Early Estimated Data");
      if (measurementStatus & 0x0080) statusFlags.push("Validated Data");
      if (measurementStatus & 0x0100) statusFlags.push("Fully Qualified Data");
      if (measurementStatus & 0x0200) statusFlags.push("Data from Storage");
      if (measurementStatus & 0x0400) statusFlags.push("Demo Data");
      if (measurementStatus & 0x0800) statusFlags.push("Test Data");
      if (measurementStatus & 0x1000) statusFlags.push("Calibration Ongoing");
      if (measurementStatus & 0x2000)
        statusFlags.push("Measurement Unavailable");
      if (measurementStatus & 0x4000)
        statusFlags.push("Questionable Measurement");
      if (measurementStatus & 0x8000) statusFlags.push("Invalid Measurement");

      if (statusFlags.length > 0) {
        console.log(`      Active Status: ${statusFlags.join(", ")}`);
      } else {
        console.log(`      Status: Normal`);
      }
    }

    // Read Device and Sensor Status field (if present - 3 bytes)
    if (hasDeviceSensorStatus && offset + 3 <= dataView.byteLength) {
      // Read as 3 bytes (24-bit value)
      const deviceStatus =
        dataView.getUint8(offset) |
        (dataView.getUint8(offset + 1) << 8) |
        (dataView.getUint8(offset + 2) << 16);
      offset += 3;

      console.log(
        `\n   🔧 DEVICE/SENSOR STATUS: 0x${deviceStatus
          .toString(16)
          .toUpperCase()}`
      );

      // Decode device status bits
      const deviceFlags = [];
      if (deviceStatus & 0x000001) deviceFlags.push("Extended Display Update");
      if (deviceStatus & 0x000002) deviceFlags.push("Equipment Malfunction");
      if (deviceStatus & 0x000004)
        deviceFlags.push("Signal Processing Irregularity");
      if (deviceStatus & 0x000008) deviceFlags.push("Inadequate Signal");
      if (deviceStatus & 0x000010) deviceFlags.push("Poor Signal");
      if (deviceStatus & 0x000020) deviceFlags.push("Low Perfusion");
      if (deviceStatus & 0x000040) deviceFlags.push("Erratic Signal");
      if (deviceStatus & 0x000080) deviceFlags.push("Non-Pulsatile Signal");
      if (deviceStatus & 0x000100) deviceFlags.push("Questionable Pulse");
      if (deviceStatus & 0x000200) deviceFlags.push("Signal Analysis Ongoing");
      if (deviceStatus & 0x000400) deviceFlags.push("Sensor Interference");
      if (deviceStatus & 0x000800) deviceFlags.push("Sensor Unconnected");
      if (deviceStatus & 0x001000) deviceFlags.push("Unknown Sensor");
      if (deviceStatus & 0x002000) deviceFlags.push("Sensor Displaced");
      if (deviceStatus & 0x004000) deviceFlags.push("Sensor Malfunctioning");
      if (deviceStatus & 0x008000) deviceFlags.push("Sensor Disconnected");

      if (deviceFlags.length > 0) {
        console.log(`      Active Issues: ${deviceFlags.join(", ")}`);
      } else {
        console.log(`      Device Status: Normal`);
      }
    }

    // Read Pulse Amplitude Index field (if present - 2 bytes)
    if (hasPulseAmplitudeIndex && offset + 2 <= dataView.byteLength) {
      const pulseAmplitudeIndexRaw = dataView.getUint16(offset, true);
      offset += 2;

      const pulseAmplitudeIndex = parseSFLOAT(pulseAmplitudeIndexRaw);

      console.log(`\n   📈 PULSE AMPLITUDE INDEX:`);
      console.log(
        `      Perfusion Level: ${
          pulseAmplitudeIndex !== null
            ? pulseAmplitudeIndex.toFixed(1) + "%"
            : "N/A"
        }`
      );
    }

    console.log(`   ========================================`);
    return {
      spO2: spO2Fast,
      pulseRate: pulseRateFast,
      timestamp: new Date(),
      // Add other fields as parsed
    };
  } catch (error) {
    console.error(`   ❌ Error parsing PLX data:`, error);
    console.log(`   ========================================`);
  }
};

// Export the parser function
export { parsePLXData };
